import { Checkbox, TableBody, TableCell, TableRow } from '@client/toolkit';
import React from 'react';
import { ExpanableTableWraper, SortDirection } from '../../components/expanable-table-wraper';
import { TableExpandableCell } from '../../components/table-expanable-cell';
import { useTranslation } from 'react-i18next';
import { BuildingContractLengthDetailsDtoPagedListDto, TenantContractLengthDetailsDto } from '@client/api';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { selectTenantSettings } from '../../../../redux/session/session.slice';
import { generatePath, useNavigate } from 'react-router-dom';
import { PortfolioPaths } from '../../../portfolio/portfolio.router';
import { MdOpenInNew } from 'react-icons/md';

export const ContractLengthTable = ({
  loading,
  data,
  onSelected,
  onSort,
  sortColumn,
  sortDirection,
}: {
  loading?: boolean;
  data: BuildingContractLengthDetailsDtoPagedListDto;
  onSelected: (ids: Set<string>) => void;
  onSort: (col: string, direction: SortDirection) => void;
  sortColumn?: string;
  sortDirection?: SortDirection;
}) => {
  const { t } = useTranslation('reporting');
  const tenantSettings = useAppSelector(selectTenantSettings);
  const navigate = useNavigate();
  if (!data) return null;
  const columns = [
    { key: 'buildingName', label: t('contractLength.building'), isSortable: true, className: 'w-[30%]' },
    { key: 'tenantsAmount', label: t('contractLength.tenants'), className: 'w-[25%]' },
    { key: 'remainingMonths', label: t('contractLength.remainingMonths'), className: 'w-[10%]' },
    { key: 'startDate', label: t('contractLength.startDate'), className: 'w-[10%]' },
    { key: 'endDate', label: t('contractLength.endDate'), className: 'w-[10%]' },
    { key: 'totalMonths', label: t('contractLength.totalMonths'), className: 'w-[10%]' },
  ];
  return (
    <ExpanableTableWraper
      loading={loading}
      onSelected={onSelected}
      onSort={onSort}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      columns={columns}
      data={data.items}
      renderBody={(
        data,
        selectedRows,
        expandedRows,
        handleToggleSelect,
        handleKeyDownSelect,
        handleToggleExpand,
        handleKeyDownExpand,
      ) => (
        <TableBody>
          {data.map((building) => (
            <React.Fragment key={building.buildingName}>
              <TableRow
                className="bg-surface-elevated/40 border-b border-surface-elevated group hover:bg-surface-elevated"
                onClick={() =>
                  navigate(generatePath(PortfolioPaths.BuildingAgreements, { buildingId: building.buildingId }))
                }
              >
                <TableCell className="px-2 py-2 align-top content-center">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedRows.has(building.buildingName)}
                    onChange={() => handleToggleSelect(building.buildingName)}
                    tabIndex={0}
                    aria-label={`Velg ${building.buildingName}`}
                    onKeyDown={(e) => handleKeyDownSelect(e, building.buildingName)}
                  />
                </TableCell>
                <TableExpandableCell
                  label={building.buildingName}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(building.buildingName);
                  }}
                  onKeyDown={(e) => handleKeyDownExpand(e, building.buildingName)}
                  isExpanded={expandedRows.has(building.buildingName)}
                  icon={<MdOpenInNew className="text-brand-primary" />}
                />
                <TableCell className="px-2 py-2 align-top">
                  {t('table.numberOfTenants', { count: building.numberOfTenants ?? 0 })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
              </TableRow>
              {expandedRows.has(building.buildingName) &&
                building.tenants.map((tenant: TenantContractLengthDetailsDto) => (
                  <React.Fragment key={`${tenant.name}-${tenant.remainingContractLengthYears}`}>
                    <TableRow className="bg-surface-base border-b border-surface-elevated">
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2 align-top">{tenant.name}</TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {t('table.contractLengthYears', { count: tenant.remainingContractLengthYears ?? 0 })}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {tenant.startDate
                          ? new Date(tenant.startDate).toLocaleDateString(
                              tenantSettings?.country?.twoLetterCode ?? 'NO',
                            )
                          : '-'}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {tenant.endDate
                          ? new Date(tenant.endDate).toLocaleDateString(tenantSettings?.country?.twoLetterCode ?? 'NO')
                          : '-'}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {t('table.contractLengthYears', { count: tenant.totalContractLengthYears ?? 0 })}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      )}
    />
  );
};
